import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type Highlight from 'lumen/models/roi-trophies/highlight';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import { TROPHY_TYPES } from 'lumen/utils/roi-trophy/constants';
import type { ActiveTrophy } from 'lumen/utils/roi-trophy/types';

export default class RoiTrophiesService extends Service {
  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked currentHighlight: Highlight | null = null;

  @tracked pricePushMilestone: number | null = null;

  cleanUp = (): void => {
    void this.fetchHighlightTask.cancelAll();
    void this.dismissHighlightTask.cancelAll();
    this.currentHighlight = null;
    this.pricePushMilestone = null;
    this.store.unloadAll('roi-trophies/highlight');
  };

  dismissPricePushMilestone = (): void => {
    if (this.pricePushMilestone === null) {
      return;
    }

    this.usageAnalytics.track('Trophy Dismissed', {
      eventTrigger: 'trophy-modal',
      product: 'pricing-manager',
      type: TROPHY_TYPES.PRICE_PUSH_MILESTONE,
      milestone: this.pricePushMilestone,
    });

    this.pricePushMilestone = null;
  };

  setPricePushMilestone = (milestone: number): void => {
    this.pricePushMilestone = milestone;

    this.usageAnalytics.track('Trophy Reached', {
      eventTrigger: 'trophy-modal',
      product: 'pricing-manager',
      type: TROPHY_TYPES.PRICE_PUSH_MILESTONE,
      milestone,
    });
  };

  dismissHighlightTask = task({ drop: true }, async () => {
    if (!this.access.hasTrophyHighlights || !this.currentHighlight) {
      return;
    }

    const { id } = this.currentHighlight;

    try {
      await this.ajax.post(
        `${this.ajax.host}/${APIGATEWAY}/highlights/${id}/seen/?subscription_id=${this.hotelAndMarketService.hotel!.id}`,
      );

      this.usageAnalytics.track('Trophy Dismissed', {
        eventTrigger: 'trophy-modal',
        product: 'pricing-manager',
        type: TROPHY_TYPES.WEEKLY_HIGHLIGHT,
        highlightType: this.currentHighlight.highlightType,
      });
    } catch (e) {
      this.errorTracking.captureException(e);
    } finally {
      this.currentHighlight = null;
    }
  });

  fetchHighlightTask = task({ restartable: true }, async (id?: number) => {
    if (!this.access.hasTrophyHighlights) {
      return;
    }

    try {
      const highlight = await this.store.queryRecord<Highlight>(
        'roi-trophies/highlight',
        {
          subscription_id: this.hotelAndMarketService.hotel!.id,
          ...(id !== undefined ? { id } : {}),
        },
      );

      if (highlight) {
        this.currentHighlight = highlight;

        this.usageAnalytics.track('Trophy Reached', {
          eventTrigger: 'trophy-modal',
          product: 'pricing-manager',
          type: TROPHY_TYPES.WEEKLY_HIGHLIGHT,
          highlightType: this.currentHighlight.highlightType,
        });
      }
    } catch (e) {
      this.errorTracking.captureException(e);
    }
  });

  get hasActiveHighlight(): boolean {
    return this.currentHighlight !== null;
  }

  get pricePushTrophy(): ActiveTrophy | null {
    return this.pricePushMilestone !== null
      ? {
          type: TROPHY_TYPES.PRICE_PUSH_MILESTONE,
          milestone: this.pricePushMilestone,
        }
      : null;
  }

  get weeklyHighlightTrophy(): ActiveTrophy | null {
    return this.currentHighlight !== null
      ? {
          type: TROPHY_TYPES.WEEKLY_HIGHLIGHT,
          highlight: this.currentHighlight,
        }
      : null;
  }
}

declare module '@ember/service' {
  interface Registry {
    'roi-trophies': RoiTrophiesService;
  }
}
